import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';
import WizardService from '../../../services/wizardService';
import generateAgreementPdf from '../../../utils/pdf/agreementPdf';

const AgreementDetailPage = () => {
  const { id } = useParams();
  const [agreement, setAgreement] = useState(null);
  const [items, setItems] = useState([]);
  const [itemNames, setItemNames] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [acceptanceLinks, setAcceptanceLinks] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [leadName, setLeadName] = useState('');
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [versions, setVersions] = useState([]);
  const [events, setEvents] = useState([]);
  const [staffMap, setStaffMap] = useState({});
  const [caseContext, setCaseContext] = useState(null);
  const [controlStatus, setControlStatus] = useState(null);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data: a, error: agreementError } = await supabase
        .from('agreements')
        .select('*')
        .eq('id', id)
        .single();
      if (agreementError) throw agreementError;
      setAgreement(a || null);

      if (a?.submission_id) {
        const { data: sub } = await supabase
          .from('wizard_submissions')
          .select('id, personal_info')
          .eq('id', a.submission_id)
          .single();
        const pi = sub?.personal_info || {};
        setLeadName(pi.fullName || pi.name || `${pi.firstName || ''} ${pi.lastName || ''}`.trim());
      } else {
        setLeadName('');
      }

      if (!a) {
        setItems([]);
        setItemNames({});
        setInvoices([]);
        setPayments([]);
        setRecipients([]);
        setVersions([]);
        setEvents([]);
        return;
      }

      const [
        itemsRes,
        recipientsRes,
        versionsRes,
        eventsRes,
        invoicesRes
      ] = await Promise.all([
        supabase.from('agreement_items').select('*').eq('agreement_id', id).order('sort_order'),
        supabase
          .from('agreement_recipients')
          .select('id, role, full_name, email, status, signing_order, signed_at, updated_at')
          .eq('agreement_id', id)
          .order('signing_order'),
        supabase
          .from('agreement_versions')
          .select('*')
          .eq('agreement_id', id)
          .order('version_number', { ascending: false }),
        supabase
          .from('agreement_events')
          .select('*')
          .eq('agreement_id', id)
          .order('created_at', { ascending: false }),
        supabase
          .from('invoices')
          .select('*')
          .eq('agreement_id', id)
          .order('created_at', { ascending: false })
      ]);

      const itemsArr = itemsRes?.data || [];
      setItems(itemsArr);

      const pkgIds = Array.from(new Set(itemsArr.filter((i) => i.item_type === 'package' && i.ref_id).map((i) => i.ref_id)));
      const loanIds = Array.from(new Set(itemsArr.filter((i) => i.item_type === 'loan' && i.ref_id).map((i) => i.ref_id)));
      const names = {};
      if (pkgIds.length) {
        const { data: pkgs } = await supabase
          .from('packages')
          .select('id, package_name, package_key, title, name')
          .in('id', pkgIds);
        (pkgs || []).forEach((p) => {
          names[`package:${p.id}`] =
            p.package_name || p.title || p.name || p.package_key || String(p.id);
        });
      }
      if (loanIds.length) {
        const { data: loans } = await supabase
          .from('loan_options')
          .select('id, loan_provider, provider_name, loan_type, title')
          .in('id', loanIds);
        (loans || []).forEach((l) => {
          names[`loan:${l.id}`] =
            l.loan_provider
              ? `${l.loan_provider}${l.loan_type ? ` — ${l.loan_type}` : ''}`
              : l.provider_name || l.title || String(l.id);
        });
      }
      setItemNames(names);

      const invoicesArr = invoicesRes?.data || [];
      setInvoices(invoicesArr);

      if (invoicesArr.length) {
        const invoiceIds = invoicesArr.map((inv) => inv.id);
        const { data: pays } = await supabase
          .from('payments')
          .select('*')
          .in('invoice_id', invoiceIds)
          .order('paid_at', { ascending: false });
        setPayments(pays || []);
      } else {
        setPayments([]);
      }
      // If invoices exist and the agreement has no acceptance/payment deadlines, use soonest invoice due_date as control status
      if (!a?.acceptance_deadline && !a?.payment_deadline && invoicesArr.length) {
        const soonestInv = invoicesArr
          .filter((inv) => inv.due_date)
          .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))[0];
        if (soonestInv?.due_date) {
          const deadlineDate = new Date(soonestInv.due_date);
          const now = new Date();
          const diffDays = Math.floor((deadlineDate - now) / (1000 * 60 * 60 * 24));
          let level = 'attention';
          if (deadlineDate < now || diffDays <= 0) level = 'critical';
          else if (diffDays <= 3) level = 'urgent';
          setControlStatus({
            level,
            deadline: deadlineDate,
            days: diffDays
          });
        }
      }

      setRecipients(recipientsRes?.data || []);
      setVersions(versionsRes?.data || []);
      setEvents(eventsRes?.data || []);
      setAcceptanceLinks({});
      setCaseContext(null);

      // Load case/pathway context if available
      if (a?.case_id || a?.pathway_id) {
        try {
          const { data: caseRow } = a?.case_id
            ? await supabase
                .from('cases')
                .select(`
                  id,
                  title,
                  pathway_id,
                  clients ( first_name, last_name ),
                  immigration_pathways:pathway_id (
                    id,
                    pathway_key,
                    countries (
                      name,
                      flag_emoji
                    )
                  )
                `)
                .eq('id', a.case_id)
                .single()
            : { data: null };

          if (caseRow) {
            setCaseContext(caseRow);
          } else if (a?.pathway_id) {
            const { data: pathRow } = await supabase
              .from('immigration_pathways')
              .select(`
                id,
                pathway_key,
                countries (
                  name,
                  flag_emoji
                )
              `)
              .eq('id', a.pathway_id)
              .single();
            if (pathRow) {
              setCaseContext({
                id: null,
                title: null,
                clients: null,
                immigration_pathways: pathRow
              });
            }
          }
        } catch (ctxErr) {
          console.warn('Agreement detail case/pathway context load failed', ctxErr);
        }
      }

      // Load unified deadline status for this agreement (Control Room alignment)
      try {
        const { data: deadlineRow } = await supabase
          .from('unified_deadlines')
          .select('deadline, deadline_type')
          .eq('entity_id', id)
          .eq('deadline_type', 'agreement')
          .single();
        if (deadlineRow?.deadline) {
          const deadlineDate = new Date(deadlineRow.deadline);
          const now = new Date();
          const diffDays = Math.floor((deadlineDate - now) / (1000 * 60 * 60 * 24));
          let level = 'attention';
          if (deadlineDate < now || diffDays <= 0) level = 'critical';
          else if (diffDays <= 3) level = 'urgent';
          setControlStatus({
            level,
            deadline: deadlineDate,
            days: diffDays
          });
        } else {
          setControlStatus(null);
        }
      } catch (deadlineErr) {
        console.warn('Agreement control status lookup failed', deadlineErr);
        setControlStatus(null);
      }

      const staffIds = Array.from(
        new Set((versionsRes?.data || []).map((v) => v.prepared_by).filter(Boolean))
      );
      if (staffIds.length) {
        const { data: staffRows, error: staffError } = await supabase
          .from('staff_profiles')
          .select('id, name, full_name, email')
          .in('id', staffIds);
        if (!staffError && staffRows) {
          const nextMap = {};
          staffRows.forEach((row) => {
            nextMap[row.id] = row.name || row.full_name || row.email || row.id;
          });
          setStaffMap(nextMap);
        } else if (staffError) {
          console.warn('Staff lookup failed', staffError);
          setStaffMap({});
        }
      } else {
        setStaffMap({});
      }
    } catch (err) {
      console.error('Agreement load error', err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (!id) return undefined;
    const channel = supabase
      .channel(`agreement_recipient_${id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'agreement_recipients',
          filter: `agreement_id=eq.${id}`
        },
        () => {
          load({ silent: true });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateStatus = async (status) => {
    setSaving(true);
    await supabase.from('agreements').update({ status, accepted_at: status === 'accepted' ? new Date().toISOString() : null }).eq('id', id);
    await load({ silent: true });
    setSaving(false);
  };

  const preparedBy = useMemo(() => {
    if (agreement?.updated_by) return agreement.updated_by;
    if (agreement?.created_by) return agreement.created_by;
    return '';
  }, [agreement]);

  const currentVersionNumber = useMemo(
    () => (versions?.length ? versions[0].version_number : null),
    [versions]
  );

  const formatEventType = (type) => {
    if (!type) return 'event';
    return type
      .toString()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const generateAcceptanceLink = async (recipientId) => {
    try {
      const { data, error } = await supabase.rpc('agreement_generate_acceptance_token', {
        p_agreement_id: id,
        p_recipient_id: recipientId,
        p_ttl_minutes: 1440
      });
      if (error || !data?.success) {
        throw new Error(data?.error || error?.message || 'Token generation failed');
      }
      const { token, expires_at: expiresAt } = data;
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const link = `${origin}/agreements/${id}/accept?token=${token}`;
      setAcceptanceLinks((prev) => ({
        ...prev,
        [recipientId]: {
          url: link,
          token,
          expiresAt
        }
      }));
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        alert('Acceptance link copied to clipboard');
      }
    } catch (err) {
      console.error('Acceptance link error', err);
      alert(err.message || 'Failed to generate acceptance link');
    }
  };

  const downloadPdf = async () => {
    if (!agreement) return;
    try {
      setPdfGenerating(true);
      const mappedItems = items.map((item) => ({
        ...item,
        name: itemNames[`${item.item_type}:${item.ref_id}`] || item.title || item.id
      }));
      generateAgreementPdf({
        agreement,
        items: mappedItems,
        invoices,
        payments,
        leadName,
        preparedBy
      });
    } catch (error) {
      console.error('Agreement PDF generation error', error);
      alert('Failed to generate PDF');
    } finally {
      setPdfGenerating(false);
    }
  };

  const generateInvoice = async () => {
    try {
      setSaving(true);
      const amount = agreement?.total_amount || items.reduce((sum, i) => sum + (i.subtotal || 0), 0);
      const due = new Date();
      due.setDate(due.getDate() + 14);
      await supabase.from('invoices').insert([{ agreement_id: id, status: 'sent', amount_due: amount, due_date: due.toISOString().slice(0, 10) }]);
      await load({ silent: true });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="admin-loading">
      <div className="admin-spinner"></div>
      <div>Loading agreement...</div>
    </div>
  );

  if (!agreement) return (
    <div className="admin-empty-state">
      <div className="admin-empty-icon">📑</div>
      <div>Agreement not found.</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">📑 Agreement Details</h1>
        <p className="text-gray-400">Manage agreement recipients, versions, and payments</p>
        <div className="text-xs text-gray-400 mt-2 space-y-1">
          <div>
            ID: <span className="font-mono">{id}</span>
          </div>
          {caseContext?.title && (
            <div className="text-gray-300">
              <span className="font-semibold text-gray-300">Case: </span>
              {caseContext.title}
              {caseContext.clients?.first_name || caseContext.clients?.last_name ? (
                <span className="text-gray-500">
                  {' '}· {[caseContext.clients?.first_name, caseContext.clients?.last_name].filter(Boolean).join(' ')}
                </span>
              ) : null}
            </div>
          )}
          {caseContext?.immigration_pathways && (
            <div className="text-gray-500">
              {caseContext.immigration_pathways.countries?.flag_emoji || ''}{' '}
              {WizardService.formatPathwayName(caseContext.immigration_pathways.pathway_key)}
              {caseContext.immigration_pathways.countries?.name ? ` · ${caseContext.immigration_pathways.countries.name}` : ''}
            </div>
          )}
          {controlStatus && (
            <div className="flex items-center gap-2 text-xs">
              <span className={`px-2 py-0.5 rounded border ${
                controlStatus.level === 'critical' ? 'bg-red-900/30 text-red-300 border-red-800' :
                controlStatus.level === 'urgent' ? 'bg-yellow-900/30 text-yellow-300 border-yellow-800' :
                'bg-blue-900/30 text-blue-300 border-blue-800'
              }`}>
                {controlStatus.level.toUpperCase()}
              </span>
              <span className="text-gray-400">
                {controlStatus.deadline ? controlStatus.deadline.toLocaleDateString() : 'No deadline'}
                {typeof controlStatus.days === 'number' ? ` (${controlStatus.days}d)` : ''}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Agreement Info */}
      <div className="bg-gray-800 rounded-lg shadow border border-gray-700 mb-6">
        <div className="p-0">
          <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white">{agreement.title || leadName || agreement.id}</h3>
            <div className="flex items-center gap-3">
              <span className={`px-2 py-1 rounded text-xs font-medium border ${agreement.status === 'accepted' ? 'bg-green-900/20 text-green-400 border-green-800' :
                  agreement.status === 'rejected' || agreement.status === 'cancelled' ? 'bg-red-900/20 text-red-400 border-red-800' :
                    agreement.status === 'sent' ? 'bg-blue-900/20 text-blue-400 border-blue-800' :
                      'bg-gray-700 text-gray-300 border-gray-600'
                }`}>
                {agreement.status}
              </span>
              <span className="text-gray-300">Total: {agreement.total_amount} {agreement.currency}</span>
            </div>
          </div>
          <div className="p-6 flex gap-3">
            <button disabled={pdfGenerating} onClick={downloadPdf} className="px-3 py-1.5 rounded text-sm font-medium transition-colors bg-gray-700 text-white border border-gray-600 hover:bg-gray-600">
              {pdfGenerating ? '⚙️ Generating…' : '📄 Download PDF'}
            </button>
            <button disabled={saving} onClick={() => updateStatus('sent')} className="px-3 py-1.5 rounded text-sm font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700">Mark Sent</button>
            <button disabled={saving} onClick={() => updateStatus('accepted')} className="px-3 py-1.5 rounded text-sm font-medium transition-colors bg-emerald-600 text-white hover:bg-emerald-700">Mark Accepted</button>
            <button disabled={saving} onClick={generateInvoice} className="px-3 py-1.5 rounded text-sm font-medium transition-colors bg-purple-600 text-white hover:bg-purple-700">Generate Invoice</button>
          </div>
        </div>
      </div>

      {/* Recipients & Signatures */}
      <div className="bg-gray-800 rounded-lg shadow border border-gray-700 mb-6">
        <div className="p-0">
          <div className="px-6 py-4 border-b border-gray-700">
            <h4 className="text-lg font-semibold text-white">👥 <span>Recipients & Signatures</span></h4>
          </div>
          <ul className="divide-y divide-gray-700">
            {recipients.map((rec) => {
              const linkInfo = acceptanceLinks[rec.id];
              return (
                <li key={rec.id} className="p-4 hover:bg-gray-700/50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium text-white">{rec.full_name} ({rec.role})</div>
                      <div className="text-sm text-gray-400 mt-1 flex items-center gap-3">
                        <span className="text-gray-300">{rec.email}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium border ${rec.status === 'signed' ? 'bg-green-900/20 text-green-400 border-green-800' : 'bg-gray-700 text-gray-300 border-gray-600'}`}>
                          {rec.status}
                        </span>
                        <span>Order: {rec.signing_order}</span>
                        {rec.signed_at && <span>• Signed: {new Date(rec.signed_at).toLocaleDateString()}</span>}
                      </div>
                      {linkInfo && (
                        <div className="mt-2 text-sm bg-gray-900 p-2 rounded border border-gray-700 text-blue-400 break-all">
                          {linkInfo.url}
                          {linkInfo.expiresAt && (
                            <span className="text-gray-500 ml-2"> (expires {new Date(linkInfo.expiresAt).toLocaleString()})</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() => generateAcceptanceLink(rec.id)}
                        className="px-3 py-1.5 rounded text-sm font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700"
                      >
                        🔗 Generate Link
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
            {recipients.length === 0 && <li className="p-8 text-center text-gray-500">No recipients defined yet.</li>}
          </ul>
        </div>
      </div>

      {/* Items */}
      <div className="bg-gray-800 rounded-lg shadow border border-gray-700 mb-6">
        <div className="p-0">
          <div className="px-6 py-4 border-b border-gray-700">
            <h4 className="text-lg font-semibold text-white">📦 <span>Items</span></h4>
          </div>
          <ul className="divide-y divide-gray-700">
            {items.map(i => (
              <li key={i.id} className="p-4 hover:bg-gray-700/50">
                <div className="flex justify-between items-center">
                  <div className="font-medium text-white">{itemNames[`${i.item_type}:${i.ref_id}`] || i.title}</div>
                  <div className="text-sm text-gray-400">
                    {i.quantity} × {i.unit_price} = <span className="text-white font-mono ml-1">{i.subtotal} {agreement.currency}</span>
                  </div>
                </div>
              </li>
            ))}
            {items.length === 0 && <li className="p-8 text-center text-gray-500">No items.</li>}
          </ul>
        </div>
      </div>

      {/* Version History */}
      <div className="bg-gray-800 rounded-lg shadow border border-gray-700 mb-6">
        <div className="p-0">
          <div className="px-6 py-4 border-b border-gray-700">
            <h4 className="text-lg font-semibold text-white">📚 <span>Version History</span></h4>
          </div>
          <ul className="divide-y divide-gray-700">
            {versions.map((version) => {
              const versionVariables =
                version && version.variables && typeof version.variables === 'object' && !Array.isArray(version.variables)
                  ? version.variables
                  : {};
              const versionSections = Array.isArray(version.sections) ? version.sections : [];
              return (
                <li key={version.id} className="p-4 hover:bg-gray-700/50">
                  <details className="group">
                    <summary className="cursor-pointer list-none">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-white">Version {version.version_number}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${version.status === 'draft' ? 'bg-gray-700 text-gray-300 border-gray-600' : 'bg-blue-900/20 text-blue-400 border-blue-800'}`}>{version.status || 'draft'}</span>
                          {currentVersionNumber === version.version_number && (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-900/20 text-green-400 border border-green-800">Current</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-400">
                          {version.created_at ? new Date(version.created_at).toLocaleString() : '-'}
                        </div>
                      </div>
                    </summary>
                    <div className="mt-2 text-sm text-gray-500 border-t border-gray-700 pt-2">
                      Prepared by {staffMap[version.prepared_by] || version.prepared_by || '—'}
                    </div>
                    <div className="mt-4 space-y-4 pl-4 border-l-2 border-gray-700">
                      {version.notes && (
                        <div className="bg-yellow-900/20 border border-yellow-800 p-3 rounded text-yellow-200 text-sm mb-4">
                          <span className="font-bold mr-2">Notes:</span>
                          {version.notes}
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Variables</div>
                        {Object.keys(versionVariables).length ? (
                          <div className="grid grid-cols-2 gap-2">
                            {Object.entries(versionVariables).map(([key, value]) => (
                              <div key={key} className="text-sm text-gray-300">
                                <span className="text-blue-400 font-mono mr-2">{key}:</span> {String(value ?? '')}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-gray-500 italic">No variables stored for this version.</div>
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Sections</div>
                        {versionSections.length ? (
                          <ul className="space-y-2">
                            {versionSections.map((section) => (
                              <li key={section.section_id || section.position} className="bg-gray-900 p-3 rounded border border-gray-700">
                                <div className="font-medium text-white mb-1">{section.title || 'Untitled section'}</div>
                                <div className="text-sm text-gray-400 line-clamp-2 font-mono text-xs">
                                  {(section.content || '').slice(0, 120)}
                                  {(section.content || '').length > 120 ? '…' : ''}
                                </div>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="text-gray-500 italic">No section snapshot recorded.</div>
                        )}
                      </div>
                    </div>
                  </details>
                </li>
              );
            })}
            {versions.length === 0 && <li className="p-8 text-center text-gray-500">No historical versions captured yet.</li>}
          </ul>
        </div>
      </div>

      {/* Activity Log */}
      <div className="bg-gray-800 rounded-lg shadow border border-gray-700 mb-6">
        <div className="p-0">
          <div className="px-6 py-4 border-b border-gray-700">
            <h4 className="text-lg font-semibold text-white">📊 <span>Activity Log</span></h4>
          </div>
          <ul className="divide-y divide-gray-700">
            {events.map((event) => (
              <li key={event.id} className="p-4 hover:bg-gray-700/50">
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <div className="font-medium text-white">{formatEventType(event.event_type)}</div>
                    <div className="text-sm text-gray-400">
                      {event.created_at ? new Date(event.created_at).toLocaleString() : '-'}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    {event.actor || 'system'}
                  </div>
                  {event.metadata && Object.keys(event.metadata || {}).length > 0 && (
                    <pre className="bg-gray-900 p-3 rounded text-xs font-mono text-gray-300 overflow-x-auto border border-gray-700 mt-2">
                      {JSON.stringify(event.metadata, null, 2)}
                    </pre>
                  )}
                </div>
              </li>
            ))}
            {events.length === 0 && <li className="p-8 text-center text-gray-500">No activity recorded yet.</li>}
          </ul>
        </div>
      </div>

      {/* Invoices & Payments */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-gray-800 rounded-lg shadow border border-gray-700">
          <div className="p-0">
            <div className="px-6 py-4 border-b border-gray-700">
              <h4 className="text-lg font-semibold text-white">💰 <span>Invoices</span></h4>
            </div>
            <ul className="divide-y divide-gray-700">
              {invoices.map(inv => (
                <li key={inv.id} className="p-4 hover:bg-gray-700/50">
                  <div className="space-y-1">
                    <div className="font-medium text-white">
                      Due: {inv.amount_due} • <span className={`${inv.status === 'paid' ? 'text-green-400' : 'text-gray-400'}`}>{inv.status}</span> • Due Date: {inv.due_date || '-'}
                    </div>
                    <div className="text-sm text-gray-500">{inv.updated_at ? new Date(inv.updated_at).toLocaleDateString() : '-'}</div>
                  </div>
                </li>
              ))}
              {invoices.length === 0 && <li className="p-8 text-center text-gray-500">No invoices.</li>}
            </ul>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg shadow border border-gray-700">
          <div className="p-0">
            <div className="px-6 py-4 border-b border-gray-700">
              <h4 className="text-lg font-semibold text-white">💳 <span>Payments</span></h4>
            </div>
            <ul className="divide-y divide-gray-700">
              {payments.map(p => (
                <li key={p.id} className="p-4 hover:bg-gray-700/50">
                  <div className="space-y-1">
                    <div className="font-medium text-white">Amount: {p.amount} • Method: {p.method || '-'} • Ref: {p.reference || '-'}</div>
                    <div className="text-sm text-gray-500">{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : '-'}</div>
                  </div>
                </li>
              ))}
              {payments.length === 0 && <li className="p-8 text-center text-gray-500">No payments.</li>}
            </ul>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex gap-3">
        {agreement.submission_id && (
          <Link to={`/admin/submissions/${agreement.submission_id}`} className="px-3 py-1.5 rounded text-sm font-medium transition-colors bg-gray-700 text-white border border-gray-600 hover:bg-gray-600">View Lead</Link>
        )}
        <Link to="/admin/agreements" className="px-3 py-1.5 rounded text-sm font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700">Back to Agreements</Link>
      </div>
    </div>
  );
};

export default AgreementDetailPage;
